import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
const ColorExplorerPage = React.lazy(() => import('./pages/color/ColorExplorerPage'));
import RouteMetadata from './components/RouteMetadata';
import SearchContentSchema from './components/SearchContentSchema';
import { COLOR_COMPARISONS } from './data/colorComparisons';
import { isValidToolPath } from './data/siteRoutes';
import { useLocation } from 'react-router-dom';
import CookieConsent from './components/CookieConsent';

// Lazy-load all routes except the main tool page
const PrivacyPolicy = React.lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/legal/TermsOfService'));
const ColorTheoryPage = React.lazy(() => import('./pages/info/ColorTheoryPage'));
const ColorModelsPage = React.lazy(() => import('./pages/info/ColorModelsPage'));
const PalettePage = React.lazy(() => import('./pages/palette/PalettePage'));
const ThemePalettesPage = React.lazy(() => import('./pages/palette/ThemePalettesPage'));
const PaletteLibraryPage = React.lazy(() => import('./pages/palette/PaletteLibraryPage'));
const SitemapPage = React.lazy(() => import('./pages/legal/SitemapPage'));
const AboutPage = React.lazy(() => import('./pages/info/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/info/ContactPage'));
const BlogIndexPage = React.lazy(() => import('./pages/blog/BlogIndexPage'));
const BlogPostPage = React.lazy(() => import('./pages/blog/BlogPostPage'));
const BrandBuilderPage = React.lazy(() => import('./pages/brand/BrandBuilderPage'));
const StudioPage = React.lazy(() => import('./pages/studio/StudioPage'));
const ColorConverterPage = React.lazy(() => import('./pages/tools/ColorConverterPage'));
const ContrastCheckerPage = React.lazy(() => import('./pages/tools/ContrastCheckerPage'));
const LinkContrastCheckerPage = React.lazy(() => import('./pages/tools/LinkContrastCheckerPage'));
const ValidationPage = React.lazy(() => import('./pages/info/ValidationPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Preserve both colors from legacy comparison URLs
const CombineRedirect: React.FC = () => {
  const { colors } = useParams<{ colors: string }>();
  return <Navigate to={colors && /^[0-9a-f]{6}-[0-9a-f]{6}$/i.test(colors) ? `/compare/${colors.toUpperCase()}` : '/404'} replace />;
};

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ComparePage = React.lazy(() => import('./pages/tools/ComparePage'));
const ResourcePage = React.lazy(() => import('./pages/info/ResourcePage'));
const ColorDirectoryPage = React.lazy(() => import('./pages/color/ColorDirectoryPage'));
const ColorComparisonPage = React.lazy(() => import('./pages/tools/ColorComparisonPage'));
const PaletteCollectionPage = React.lazy(() => import('./pages/palette/PaletteCollectionPage'));
function ComparisonRoute() {
  const { colors } = useParams();
  return COLOR_COMPARISONS.some(guide => guide.slug === colors)
    ? <ColorComparisonPage />
    : <ValidTool><ComparePage /></ValidTool>;
}
function ValidTool({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return isValidToolPath(pathname) ? children : <NotFoundPage />;
}
const App: React.FC = () => {
  return (
    <Router>
      <RouteMetadata />
      <SearchContentSchema />
      <Suspense fallback={<div className="min-h-screen bg-surface" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/compare/:colors" element={<ComparisonRoute />} />
          <Route path="/colors" element={<ColorDirectoryPage />} />
          <Route path="/color-palettes" element={<PaletteCollectionPage />} />
          <Route path="/color-palettes/:slug" element={<PaletteCollectionPage />} />
          {["/image-color-picker", "/methodology", "/editorial-policy"].map(path => <Route key={path} path={path} element={<ResourcePage />} />)}
          <Route path="/color/:hex" element={<ValidTool><ColorExplorerPage /></ValidTool>} />
          <Route path="/combine/:colors" element={<CombineRedirect />} />
          <Route path="/palette/:colors" element={<ValidTool><PalettePage /></ValidTool>} />
          <Route path="/themes/:hex" element={<ValidTool><ThemePalettesPage /></ValidTool>} />
          <Route path="/palette-library/:hex" element={<ValidTool><PaletteLibraryPage /></ValidTool>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/theory" element={<ColorTheoryPage />} />
          <Route path="/models" element={<ColorModelsPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/brand-builder" element={<BrandBuilderPage />} />
          <Route path="/converter" element={<ColorConverterPage />} />
          <Route path="/contrast-checker" element={<ContrastCheckerPage />} />
          <Route path="/link-contrast-checker" element={<LinkContrastCheckerPage />} />
          <Route path="/validation" element={<ValidationPage />} />
          <Route path="/studio" element={<StudioPage />} />
          <Route path="/studio/:colors" element={<ValidTool><StudioPage /></ValidTool>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <CookieConsent />
    </Router>
  );
};

export default App;
